import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VaFileInputMultiple } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { isEmpty } from 'lodash-es';
import {
  MISSING_PASSWORD_ERROR,
  MISSING_FILE,
  MISSING_ADDITIONAL_INFO,
  BACKEND_INCORRECT_PASSWORD_MSG,
  INCORRECT_PASSWORD_ERROR,
  filePresenceValidation,
} from '../validation';
import {
  useFileUpload,
  getFileError,
  simulateUploadMultiple,
  getMockFileData,
  VaProgressUploadAnnounce,
  getFileLimitErrorMessage,
} from './vaFileInputFieldHelpers';
import vaFileInputFieldMapping from './vaFileInputFieldMapping';

import { errorManager } from '../utilities/file/passwordErrorState';

export const sanitizeMultipleFileInputFormData = (formData = []) =>
  formData.filter(filePresenceValidation);

export const isPersistableUploadResult = uploadedFile =>
  !!uploadedFile?.file &&
  !!uploadedFile?.confirmationCode &&
  !uploadedFile?.errorMessage;

export const getSlotFieldIndexes = (errors = [], formData = []) =>
  errors
    .map((error, i) =>
      error || !filePresenceValidation(formData?.[i]) ? null : i,
    )
    .filter(i => i !== null);

const VaFileInputMultipleField = props => {
  const { uiOptions = {}, childrenProps } = props;
  const [encrypted, setEncrypted] = useState([]);
  const [errors, setErrors] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [percentsUploaded, setPercentsUploaded] = useState([]);
  const [passwordSubmissionSuccessArray, setPasswordSubmissionSuccessArray] =
    useState([]);
  const [initPoll, setInitPoll] = useState(true);
  const needsPollRef = useRef(false);
  const dispatch = useDispatch();
  const featureToggles = useSelector(state => state?.featureToggles);
  const mappedProps = vaFileInputFieldMapping(props);
  const { percentUploaded, handleUpload } = useFileUpload(
    uiOptions,
    mappedProps.accept,
    dispatch,
    { featureToggles },
  );
  const componentRef = useRef(null);

  const isPersistedUploadedFile = file => filePresenceValidation(file);
  const sanitizeFormData = formData =>
    sanitizeMultipleFileInputFormData(formData);

  const updateFormData = formData => {
    childrenProps.onChange(sanitizeFormData(formData));
  };

  // Clean up stale placeholder objects from persisted Redux/form state.
  useEffect(() => {
    if (!Array.isArray(childrenProps.formData)) {
      return;
    }
    const sanitized = sanitizeFormData(childrenProps.formData);
    if (sanitized.length !== childrenProps.formData.length) {
      childrenProps.onChange(sanitized);
    }
  }, [childrenProps.formData]);

  const requestController = useRef({});
  // error passed to va-file-input-multiple parent
  const [wrapperError, setWrapperError] = useState(null);

  useEffect(() => {
    setWrapperError(mappedProps.error);
  }, [mappedProps.error]);

  // if prefill, initialize values
  useEffect(() => {
    const doPrefill =
      Array.isArray(mappedProps.uploadedFiles) && componentRef.current;
    if (doPrefill) {
      const nulls = new Array(childrenProps.formData.length).fill(null);
      setErrors([...nulls]);
      setEncrypted([...nulls]);
      childrenProps.formData.forEach((_, i) =>
        errorManager.addPasswordInstance(i),
      );
      setCurrentIndex(childrenProps.formData.length - 1);
      componentRef.current.value = mappedProps.uploadedFiles;
    }
  }, []);

  // update the percent uploaded array
  useEffect(() => {
    const _percents = [...percentsUploaded];
    _percents[currentIndex] = percentUploaded;
    setPercentsUploaded(_percents);
  }, [percentUploaded]);

  function getSlotContent() {
    const slot = componentRef.current?.shadowRoot
      ?.querySelector('va-file-input')
      ?.shadowRoot?.querySelector('slot');
    if (slot) {
      const [slotContent] =
        slot?.assignedElements?.({ flatten: true }) ||
        slot?.assignedNodes().filter(n => n.nodeType === 1);
      return !!slotContent;
    }
    return false;
  }

  // don't check for error on load or after replacing a file
  function updateAdditionalInputs(checkForError = false) {
    childrenProps.formData.forEach((_, index) => {
      const instance = componentRef.current.shadowRoot.getElementById(
        `instance-${index}`,
      );
      if (instance) {
        const slot = instance.shadowRoot.querySelector('slot');
        if (!slot) return;
        const [slotContent] =
          slot?.assignedElements?.({ flatten: true }) ||
          slot?.assignedNodes().filter(n => n.nodeType === 1);
        if (slotContent) {
          setInitPoll(false);
          const file = childrenProps.formData[index];
          const _isEmpty = !file || (file && isEmpty(file.additionalData));
          // associate file with input for screen reader
          if (slotContent.firstElementChild) {
            slotContent.firstElementChild.setAttribute(
              'message-aria-describedby',
              file.name,
            );
          }
          // component missing additional data and has been touched and instance not already in error state
          const additionalInputError =
            _isEmpty &&
            checkForError &&
            index < errorManager.getLastTouched() &&
            !instance.getAttribute('error')
              ? childrenProps?.uiSchema?.['ui:errorMessages']
                  ?.additionalInput || MISSING_ADDITIONAL_INFO
              : '';
          const additionalData = file?.additionalData || null;
          uiOptions.additionalInputUpdate(
            slotContent.firstElementChild,
            additionalInputError,
            additionalData,
          );
        }
      }
    });
  }

  function sleep(delay) {
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  // poll on load until slot content renders
  async function poll() {
    const WAIT = 50;
    const MAXLOOP = 3000 / WAIT;
    for (let attempt = 0; attempt < MAXLOOP; attempt++) {
      const ready = getSlotContent();
      if (ready) {
        updateAdditionalInputs();
        // If validation ran while polling (e.g., Continue clicked
        // before poll completed), check for errors now.
        if (mappedProps.error) {
          updateAdditionalInputs(true);
        }
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(WAIT);
    }
  }

  // update the additional inputs with prefill data (if present)
  // set errors on additional inputs (if present)
  useEffect(() => {
    // load or replacing a file
    if (initPoll || needsPollRef.current) {
      poll();
      needsPollRef.current = false;
      // error set in validation
    } else {
      updateAdditionalInputs(true);
    }
  }, [childrenProps.formData, mappedProps.error]);

  // update errors array with missing file error that was set in validator
  useEffect(() => {
    if (mappedProps.error === MISSING_FILE) {
      const _errors = [...errors];
      _errors[0] =
        childrenProps?.uiSchema?.['ui:errorMessages']?.required || MISSING_FILE;
      setErrors(_errors);
    }
  }, [mappedProps.error]);

  const assignFileUploadToStore = (uploadedFile, index) => {
    if (!uploadedFile) return;

    const { file, ...rest } = uploadedFile;

    const { name, size, type } = file;

    const newFile = {
      ...rest,
      name,
      size,
      type,
    };

    const existingFile = childrenProps.formData[index];
    // if existingFile is not null then either
    // 1. it is a placeholder for an encrypted file where additional info was added before the password OR
    // 2. it is a file that is being replaced
    let files;
    if (existingFile) {
      if (encrypted[index] && existingFile.additionalData) {
        newFile.additionalData = existingFile.additionalData;
      } else {
        // flag so the effect polls for updated slot content before resetting
        needsPollRef.current = true;
      }
      files = [...childrenProps.formData];
      files[index] = newFile;
    } else {
      files = [...childrenProps.formData, newFile];
    }
    updateFormData(files);
  };

  // set errors and related statuses based on upload result
  const handleUploadErrors = ({ errorMessage, isEncrypted }, index) => {
    const _errors = [...errors];
    const hasWrongPassword =
      isEncrypted && errorMessage === BACKEND_INCORRECT_PASSWORD_MSG;
    errorManager.setHasWrongPassword(index, hasWrongPassword);
    const _error = hasWrongPassword ? ' ' : errorMessage;
    // if we have a missing password error - set a parent level error too (va-file-input-multiple)
    setWrapperError(() => (hasWrongPassword ? `${Math.random()}` : null));
    // set child level error (va-file-input)
    _errors[index] = _error;

    setErrors(_errors);
    // file was encrypted
    if (isEncrypted) {
      const _passwordSubmissionSuccessArray = [
        ...passwordSubmissionSuccessArray,
      ];
      _passwordSubmissionSuccessArray[index] = !errorMessage;
      setPasswordSubmissionSuccessArray(_passwordSubmissionSuccessArray);
    }
  };

  const handleFileProcessing = (uploadedFile, index) => {
    if (
      !uploadedFile ||
      !uploadedFile.file ||
      (requestController.current[index] &&
        requestController.current[index]?.signal?.aborted)
    )
      return;
    // remove controller if file has been uploaded
    if (!uploadedFile.uploading) {
      requestController.current[index] = null;
    }
    handleUploadErrors(uploadedFile, index);
    const hasUploadError = !isPersistableUploadResult(uploadedFile);
    errorManager.setFileCheckError(index, hasUploadError);
    if (hasUploadError) {
      return;
    }
    uiOptions.onUploadSuccess?.({ uploadedFile, index });
    assignFileUploadToStore(uploadedFile, index);
  };

  const handleFileAdded = async (file, index) => {
    const { fileError, encryptedCheck } = await getFileError(
      file,
      uiOptions,
      childrenProps.formData,
    );
    const _errors = [...errors];

    if (fileError) {
      _errors[index] = fileError;
      setErrors(_errors);
      requestController.current[index] = null;
      errorManager.setFileCheckError(index, true);
      return;
    }

    // file ok
    errorManager.setFileCheckError(index, false);
    _errors[index] = null;
    setErrors(_errors);

    const _encrypted = [...encrypted];
    _encrypted[index] = encryptedCheck;
    setEncrypted(_encrypted);

    // keep track of potential missisng password errors
    errorManager.addPasswordInstance(index, encryptedCheck);

    // mock form has no back-end but we want to add files and simulate progress of upload
    if (uiOptions.skipUpload && !encryptedCheck) {
      simulateUploadMultiple(
        setPercentsUploaded,
        percentsUploaded,
        index,
        childrenProps,
        file,
        requestController.current[index],
      );
      return;
    }

    // this file not encrypted - upload right now
    if (!encryptedCheck) {
      handleUpload(
        file,
        handleFileProcessing,
        null,
        index,
        requestController.current[index],
      );
    }
  };

  function removeOneFromArray(array, index) {
    return [...array].toSpliced(index, 1);
  }

  const handleFileRemoved = index => {
    setErrors(removeOneFromArray(errors, index));
    errorManager.removeFileCheckError(index);
    errorManager.removeInternalFileInputError(index);
    errorManager.removeInstance(index);
    setPasswordSubmissionSuccessArray(
      removeOneFromArray(passwordSubmissionSuccessArray, index),
    );
    setEncrypted(removeOneFromArray(encrypted, index));
    setPercentsUploaded(removeOneFromArray(percentsUploaded, index));

    const formData = removeOneFromArray(childrenProps.formData, index);
    updateFormData(formData);
  };

  const handleChange = e => {
    const { detail } = e;
    const { action, state, file, index } = detail;
    switch (action) {
      case 'FILE_ADDED': {
        requestController.current[index] = new AbortController();
        errorManager.setInternalFileInputErrors(index, false);
        handleFileAdded(file, index);
        setCurrentIndex(index);
        break;
      }
      case 'FILE_UPDATED': {
        requestController.current[index] = new AbortController();
        handleFileAdded(file, index);
        setCurrentIndex(index);
        break;
      }
      case 'PASSWORD_UPDATE': {
        // reset in case user is replacing one encrypted file with another
        const _passwordSubmissionSuccessArray = [
          ...passwordSubmissionSuccessArray,
        ];
        _passwordSubmissionSuccessArray[index] = null;
        setPasswordSubmissionSuccessArray(_passwordSubmissionSuccessArray);
        setCurrentIndex(index);
        const { file: passwordFile, password } = state[index];
        errorManager.resetInstance(index);
        if (uiOptions.skipUpload) {
          const formData = [
            ...childrenProps.formData,
            getMockFileData(passwordFile),
          ];
          setTimeout(() => {
            childrenProps.onChange(formData);
            handleUploadErrors(
              { errorMessage: null, isEncrypted: true },
              index,
            );
          }, 500);
        } else {
          handleUpload(
            passwordFile,
            handleFileProcessing,
            password,
            index,
            requestController.current[index],
          );
        }
        break;
      }
      case 'FILE_REMOVED':
        // if this is a cancelled upload, call abort on the controller
        requestController.current[index]?.abort?.();
        handleFileRemoved(index);
        break;
      default:
        break;
    }
  };

  // get the va-file-input instances
  function getFileInputInstanceIndex(e) {
    const [vaFileInput] = e
      .composedPath()
      .filter(el => el.tagName === 'VA-FILE-INPUT');

    let els = [];
    if (componentRef.current?.shadowRoot) {
      els = Array.from(
        componentRef.current.shadowRoot.querySelectorAll('va-file-input'),
      );
    }
    return els.findIndex(el => el.id === vaFileInput.id);
  }

  const handleAdditionalInput = e => {
    const index = getFileInputInstanceIndex(e);
    if (!isPersistedUploadedFile(childrenProps.formData[index])) {
      return;
    }
    // update the formData
    if (mappedProps.handleAdditionalInput) {
      const payload = mappedProps.handleAdditionalInput(e);
      const updatedFormData = [...(childrenProps.formData || [])];
      updatedFormData[index] = {
        ...updatedFormData[index],
        additionalData: payload,
      };
      updateFormData(updatedFormData);
    }
  };

  const handleInternalFileInputError = e => {
    const index = getFileInputInstanceIndex(e);
    errorManager.setInternalFileInputErrors(index, true);
    const { maxFileCount } = uiOptions;
    const fileCountError =
      index + 1 > (maxFileCount || Infinity)
        ? getFileLimitErrorMessage(maxFileCount)
        : null;
    const _errors = [...errors];
    // let file limit error take priority over an internal error
    _errors[index] = fileCountError || e.detail.error;
    setErrors(_errors);
  };

  // get the password errors for any relevant instances
  const passwordErrors = errorManager.getPasswordInstances().map(instance => {
    let status = null;
    if (instance) {
      if (instance.getHasMissingPasswordError())
        status = MISSING_PASSWORD_ERROR;
      if (instance.getHasWrongPassword()) status = INCORRECT_PASSWORD_ERROR;
    }
    return status;
  });

  // Don't render additional-input slots unless a valid persisted file exists.
  const slotFieldIndexes = getSlotFieldIndexes(errors, childrenProps.formData);
  return (
    <>
      <VaProgressUploadAnnounce
        uploading={percentsUploaded.some(percent => !!percent)}
      />
      <VaFileInputMultiple
        data-dd-privacy="mask"
        {...mappedProps}
        error={wrapperError}
        ref={componentRef}
        encrypted={encrypted}
        onVaMultipleChange={handleChange}
        onVaFileInputError={handleInternalFileInputError}
        errors={errors}
        passwordSubmissionSuccessList={passwordSubmissionSuccessArray}
        percentUploaded={percentsUploaded}
        passwordErrors={passwordErrors}
        onVaSelect={handleAdditionalInput}
        slotFieldIndexes={slotFieldIndexes}
      >
        {mappedProps.additionalInput && (
          <div className="additional-input-container">
            {mappedProps.additionalInput({
              labels: uiOptions.additionalInputLabels,
              title: uiOptions.additionalInputTitle,
            })}
          </div>
        )}
      </VaFileInputMultiple>
    </>
  );
};

export default VaFileInputMultipleField;
